/**
 * Google sign-in on the customer and partner portals. The button shows only while the API says Google is available on
 * this host (per-surface flag + configured OAuth client). The API's callback redirects back to /sign-in with what the page
 * cannot read for itself: why Google was refused (?error=), or which second-factor step comes next (?mfa=), because
 * GET /me answers 401 while a second factor is pending. Both only choose what the page shows; the API still enforces them.
 */
import { useEffect, useState } from 'react';
import { Button } from '@qm/ui';
import { bff } from './bff';

export const GOOGLE_START_URL = '/bff/v1/identity/google/start';

/** True once the API has confirmed Google sign-in is available on this host. */
export function useGoogleSignin(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    bff<{ enabled: boolean }>('/v1/identity/google/status').then((r) => { if (live) setEnabled(r?.enabled === true); }, () => undefined);
    return () => { live = false; };
  }, []);
  return enabled;
}

const NAMED_ERRORS: Readonly<Record<string, string>> = {
  no_account: 'no_account',
  email_not_verified: 'email_not_verified',
  account_conflict: 'account_conflict',
  account_disabled: 'no_access',
  no_membership: 'no_access',
  wrong_surface: 'no_access',
};

/** Catalog suffix for a `?error=` reason (`googleError.<suffix>`); null when the page was not refused. */
export function googleErrorSuffix(reason: string | null): string | null {
  if (!reason) return null;
  return Object.hasOwn(NAMED_ERRORS, reason) ? NAMED_ERRORS[reason]! : 'failed';
}

export interface GoogleMfaResume { next: 'mfa' | 'enrol_mfa'; methods?: string[] }

/** `?mfa=mfa&methods=totp,passkey` or `?mfa=enrol_mfa` after the Google callback. Anything else is ignored. */
export function googleMfaResume(params: URLSearchParams): GoogleMfaResume | null {
  const next = params.get('mfa');
  if (next === 'enrol_mfa') return { next };
  if (next !== 'mfa') return null;
  const methods = (params.get('methods') ?? '').split(',').filter((m) => m === 'totp' || m === 'passkey');
  return methods.length ? { next, methods } : { next };
}

/** The Google "G", in Google's own colours as its branding rules require. */
function GoogleMark() {
  return (
    <svg className="qm-google-button__mark" viewBox="0 0 18 18" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

export function GoogleButton({ label, onClick, disabled, testId }: { label: string; onClick: () => void; disabled?: boolean; testId: string }) {
  return (
    <Button type="button" className="qm-google-button" onClick={onClick} disabled={disabled} data-testid={testId}>
      <GoogleMark />
      <span>{label}</span>
    </Button>
  );
}
